import * as React from 'react';
import { match } from '@formatjs/intl-localematcher';
import { useIntl } from 'react-intl';

export const supportedLocales = [
  'ar-SA',
  'de-DE',
  'en',
  'es-ES',
  'fr-FR',
  'he-IL',
  'hi-IN',
  'it-IT',
  'ja-JP',
  'ko-KR',
  'nb-NO',
  'nl-NL',
  'pt-BR',
  'ro-RO',
  'ru-RU',
  'sv-SE',
  'zh-CN',
  'zh-TW',
] as const;

export const Locales = [...supportedLocales, 'en-XA', 'xx-HA'] as const;
export const RTLLocales: ReadonlyArray<Locale> = ['ar-SA', 'he-IL', 'xx-HA'] as const;
export type Locale = (typeof Locales)[number];

export function isLocaleRTL(locale: Locale) {
  return RTLLocales.includes(locale);
}

export function useLocaleNames() {
  const { locale } = useIntl();
  return React.useMemo(() => new Intl.DisplayNames([locale], { type: 'language' }), [locale]);
}

export function getLocaleHtml(requestedLang?: Locale | null): Locale {
  let requestedLangs = (navigator.languages ? navigator.languages : [navigator.language]) as [Locale];
  if (requestedLang) {
    requestedLangs = [requestedLang];
  }

  const fixedLangs = requestedLangs.map((lang) => lang.replace(/_/g, '-'));

  const locale = match(fixedLangs, [...Locales], 'en') as Locale;

  return locale;
}

const displayNames = supportedLocales.reduce(
  (memo, locale) => {
    const localDisplayNames = new Intl.DisplayNames([locale], { type: 'language' });

    const others = supportedLocales.reduce(
      (memo, otherLocale) => {
        const localeOnly = otherLocale.split('-')[0];
        memo[otherLocale] = localDisplayNames.of(localeOnly) as string;
        return memo;
      },
      {} as Record<Locale, string>
    );

    memo[locale] = {
      collator: new Intl.Collator(locale),
      others,
    };

    return memo;
  },
  {} as Record<Locale, { collator: Intl.Collator; others: Record<Locale, string> }>
);

const countryWithLocalName: Record<Locale, string> = {
  // nb: This is hardcoded so that we do not need to load all language polyfills.
  'de-DE': 'Deutsch',
  en: 'English',
  'ar-SA': 'العربية',
  'zh-CN': '中文',
  'zh-TW': '中文',
  'ko-KR': '한국어',
  'es-ES': 'español',
  'fr-FR': 'français',
  'he-IL': 'עברית',
  'hi-IN': 'हिन्दी',
  'it-IT': 'italiano',
  'ja-JP': '日本語',
  'nl-NL': 'Nederlands',
  'nb-NO': 'norsk bokmål',
  'pt-BR': 'português',
  'ro-RO': 'română',
  'ru-RU': 'русский',
  'sv-SE': 'svenska',
  'en-XA': 'English',
  'xx-HA': 'English',
};

export function getLanguageHandler(knownLocal: Locale | undefined) {
  const locale = knownLocal || getLocaleHtml();
  const data = supportedLocales
    .map((slug) => {
      return { localName: countryWithLocalName[slug], name: displayNames[locale].others[slug], locale: slug };
    })
    .sort((a, b) => displayNames[locale].collator.compare(a.name, b.name));

  return data;
}
